import type { Vec3 } from "../modeling/faceBasis.ts";
import { coplanarSurfaceRegionFromHit } from "../modeling/surfaceSelection.ts";
import type { ViewportSurfaceHit } from "../modeling/viewportSurfaceRaycast.ts";

export interface SurfaceSelectionHighlightFace {
  key: string;
  objectId: string;
  vertices: readonly Vec3[];
}

export interface SurfaceSelectionHighlightSurface {
  key: string;
  objectId: string;
  faces: readonly SurfaceSelectionHighlightFace[];
}

export interface SurfaceSelectionHighlightSnapshot {
  hover: SurfaceSelectionHighlightSurface | null;
  selected: readonly SurfaceSelectionHighlightSurface[];
}

const EMPTY_SNAPSHOT: SurfaceSelectionHighlightSnapshot = Object.freeze({
  hover: null,
  selected: Object.freeze([]),
});

let snapshot: SurfaceSelectionHighlightSnapshot = EMPTY_SNAPSHOT;
const listeners = new Set<() => void>();

export function subscribeSurfaceSelectionHighlight(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSurfaceSelectionHighlightSnapshot(): SurfaceSelectionHighlightSnapshot {
  return snapshot;
}

export function surfaceSelectionHighlightSurfaceFromHit(
  hit: ViewportSurfaceHit,
): SurfaceSelectionHighlightSurface | null {
  const region = coplanarSurfaceRegionFromHit(hit);
  if (region) {
    return {
      key: `${hit.objectId}:region:${region.faceIndices.join(",")}`,
      objectId: hit.objectId,
      faces: region.faces.map((face) => ({
        key: `${hit.objectId}:face:${face.index}`,
        objectId: hit.objectId,
        vertices: copyVertices(face.vertices),
      })),
    };
  }

  const vertices = hit.face?.vertices ?? hit.triangle?.vertices;
  if (!vertices || vertices.length < 3) return null;
  return {
    key:
      hit.face?.index !== undefined
        ? `${hit.objectId}:face:${hit.face.index}`
        : `${hit.objectId}:triangle:${verticesKey(vertices)}`,
    objectId: hit.objectId,
    faces: [
      {
        key:
          hit.face?.index !== undefined
            ? `${hit.objectId}:face:${hit.face.index}`
            : `${hit.objectId}:triangle:${verticesKey(vertices)}`,
        objectId: hit.objectId,
        vertices: copyVertices(vertices),
      },
    ],
  };
}

export function setSurfaceSelectionHighlightHover(
  hit: ViewportSurfaceHit | null,
): void {
  const hover = hit ? surfaceSelectionHighlightSurfaceFromHit(hit) : null;
  if (sameSurface(snapshot.hover, hover)) return;
  commit({ ...snapshot, hover });
}

export function setSurfaceSelectionHighlightSelection(
  hits: readonly ViewportSurfaceHit[],
): void {
  const selected = dedupeSurfaces(
    hits
      .map(surfaceSelectionHighlightSurfaceFromHit)
      .filter(
        (surface): surface is SurfaceSelectionHighlightSurface =>
          surface !== null,
      ),
  );
  if (sameSurfaceList(snapshot.selected, selected)) return;
  commit({ ...snapshot, selected });
}

export function clearSurfaceSelectionHighlight(): void {
  if (snapshot === EMPTY_SNAPSHOT) return;
  commit(EMPTY_SNAPSHOT);
}

function commit(next: SurfaceSelectionHighlightSnapshot): void {
  snapshot =
    next.hover === null && next.selected.length === 0 ? EMPTY_SNAPSHOT : next;
  for (const listener of listeners) listener();
}

function dedupeSurfaces(
  surfaces: readonly SurfaceSelectionHighlightSurface[],
): SurfaceSelectionHighlightSurface[] {
  const seen = new Set<string>();
  const next: SurfaceSelectionHighlightSurface[] = [];
  for (const surface of surfaces) {
    if (seen.has(surface.key)) continue;
    seen.add(surface.key);
    next.push(surface);
  }
  return next;
}

function sameSurface(
  a: SurfaceSelectionHighlightSurface | null,
  b: SurfaceSelectionHighlightSurface | null,
): boolean {
  return a?.key === b?.key;
}

function sameSurfaceList(
  a: readonly SurfaceSelectionHighlightSurface[],
  b: readonly SurfaceSelectionHighlightSurface[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i]?.key !== b[i]?.key) return false;
  }
  return true;
}

function copyVertices(vertices: readonly Vec3[]): Vec3[] {
  return vertices.map((vertex) => [vertex[0], vertex[1], vertex[2]]) as Vec3[];
}

function verticesKey(vertices: readonly Vec3[]): string {
  return vertices
    .map((vertex) => `${vertex[0]},${vertex[1]},${vertex[2]}`)
    .join("|");
}
